/**
 * ContentPlannerStats
 *
 * The "Ready to Create" summary card displayed at Step 3 of the
 * "I have keywords" flow. Shows:
 *  - How many posts will be created + template in use
 *  - Auto-publish toggle (live publish vs. leave it in the tool)
 *  - Bridge publisher selector (when available)
 *  - Target word count input (hidden in Quick Create v2, when `hideWordCount` —
 *    the selected template's built-in length drives generation)
 *  - "Publish on" datetime picker (Quick Create v2 only, when `showSchedule`)
 *  - Create Blog Post button
 */

import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Loader2, Rocket, Globe, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, Tag, Switch } from 'antd';

export interface ContentPlannerStatsProps {
  // ── Keyword summary ───────────────────────────────────────────────────────
  keywordCount: number;
  currentTemplateName?: string;

  // ── Word count ────────────────────────────────────────────────────────────
  targetWordCount: number;
  onWordCountChange: (value: number) => void;
  /** Quick Create v2 (#1307): hide the manual word-count input. The selected
   *  template's built-in length (default_config.targetWordCount) drives
   *  generation, so the control is redundant in the simplified flow. */
  hideWordCount?: boolean;

  // ── Auto-publish ──────────────────────────────────────────────────────────
  autoPublishEnabled: boolean;
  onAutoPublishChange: (enabled: boolean) => void;

  // ── Bridge publisher ──────────────────────────────────────────────────────
  bridgeEnabled: boolean;
  useBridge: boolean;
  onUseBridgeToggle: () => void;

  // ── Publish scheduling (Quick Create v2, #row3) ───────────────────────────
  /** When true, show a "Publish on" datetime picker at create time. */
  showSchedule?: boolean;
  /** datetime-local value (empty = publish as soon as generation completes). */
  scheduledDate?: string;
  onScheduledDateChange?: (value: string) => void;

  // ── Create action ─────────────────────────────────────────────────────────
  isSubmitting: boolean;
  onCreateBlogs: () => void;
}

export const ContentPlannerStats: React.FC<ContentPlannerStatsProps> = ({
  keywordCount,
  currentTemplateName,
  targetWordCount,
  onWordCountChange,
  hideWordCount = false,
  autoPublishEnabled,
  onAutoPublishChange,
  bridgeEnabled,
  useBridge,
  onUseBridgeToggle,
  showSchedule = false,
  scheduledDate = '',
  onScheduledDateChange,
  isSubmitting,
  onCreateBlogs,
}) => {
  const summaryText = (() => {
    if (keywordCount === 0) return 'Enter keywords above to continue';
    if (keywordCount === 1) return '1 blog post will be created';
    return `1 blog post will be created (first keyword = primary, +${keywordCount - 1} secondary)`;
  })();

  const templateSuffix =
    currentTemplateName && keywordCount > 0 ? ` using "${currentTemplateName}" template` : '';

  return (
    <Card className="p-6 bg-coral-light border-coral/30">
      {/* ── Header row ─────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-6 h-6 rounded-full bg-coral text-white flex items-center justify-center text-sm font-medium">
            3
          </div>
          <div>
            <h3 className="font-semibold">Ready to Create</h3>
            <p className="text-sm text-muted-foreground">
              {summaryText}
              {templateSuffix}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* Auto-publish toggle */}
          <div className="flex items-center gap-2 px-3 py-2 bg-card rounded-lg border border-coral/20">
            <Globe className="h-4 w-4 text-teal" />
            <span className="text-sm font-medium">Auto-publish</span>
            <Tooltip title="Articles will be published as drafts to WordPress after generation completes">
              <span className="text-muted-foreground cursor-help text-xs">(drafts)</span>
            </Tooltip>
            <Switch
              checked={autoPublishEnabled}
              onChange={onAutoPublishChange}
              disabled={isSubmitting}
              size="small"
            />
          </div>

          {/* Bridge publisher selector */}
          {bridgeEnabled && (
            <div className="flex items-center gap-2 px-3 py-2 bg-card rounded-lg border border-border">
              <Zap className={cn('h-4 w-4', useBridge ? 'text-teal' : 'text-muted-foreground')} />
              <span className="text-sm font-medium">Publisher</span>
              <Tooltip
                title={
                  useBridge
                    ? 'Bridge: uses your page builder (Divi, Elementor, etc.) for native formatting'
                    : 'Legacy: uses standard WordPress publishing'
                }
              >
                <Tag
                  color={useBridge ? 'cyan' : 'default'}
                  className="!text-xs !px-2 !py-0 !m-0 cursor-pointer select-none"
                  onClick={() => !isSubmitting && onUseBridgeToggle()}
                >
                  {useBridge ? 'Bridge' : 'Legacy'}
                </Tag>
              </Tooltip>
            </div>
          )}

          {/* Create button */}
          <Button
            onClick={onCreateBlogs}
            disabled={isSubmitting || keywordCount === 0}
            size="lg"
            className="bg-coral hover:bg-coral-hover text-white"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Rocket className="h-4 w-4 mr-2" />
                Create Blog Post
              </>
            )}
          </Button>
        </div>
      </div>

      {/* ── Word count + schedule row ────────────────────────────────────
          Rendered when keywords are entered AND there is something to show —
          the word-count input (hidden in Quick Create v2, #1307) and/or the
          "Publish on" picker (Quick Create v2 only). */}
      {keywordCount > 0 && (!hideWordCount || showSchedule) && (
        <div className="mt-6 pt-6 border-t border-coral/20">
          <div className="flex items-center gap-4">
            {!hideWordCount && (
              <div className="flex items-center gap-2">
                <Label htmlFor="targetWordCount" className="text-sm font-medium whitespace-nowrap">
                  Word Count:
                </Label>
                <Input
                  id="targetWordCount"
                  type="number"
                  value={targetWordCount}
                  onChange={(e) =>
                    onWordCountChange(
                      Math.max(500, Math.min(5000, parseInt(e.target.value) || 1800))
                    )
                  }
                  min={500}
                  max={5000}
                  step={100}
                  disabled={isSubmitting}
                  className="w-24"
                />
                <span className="text-xs text-muted-foreground">(500–5000)</span>
              </div>
            )}

            {showSchedule && (
              <div className="flex items-center gap-2">
                <Label htmlFor="scheduledDate" className="text-sm font-medium whitespace-nowrap">
                  Publish on:
                </Label>
                <Input
                  id="scheduledDate"
                  type="datetime-local"
                  value={scheduledDate}
                  onChange={(e) => onScheduledDateChange?.(e.target.value)}
                  disabled={isSubmitting}
                  className="w-56"
                />
                {scheduledDate ? (
                  <button
                    type="button"
                    onClick={() => onScheduledDateChange?.('')}
                    disabled={isSubmitting}
                    className="text-xs text-muted-foreground hover:text-foreground underline"
                  >
                    Clear
                  </button>
                ) : (
                  <span className="text-xs text-muted-foreground">(optional — leave blank to publish when ready)</span>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
};
